import { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import {
  BookOpen, Briefcase, FileText, Plus, Settings, Sparkles, Sun, Users,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { isTwa } from '../../utils/platform';
import { CreateMenu } from './CreateMenu';

/** Five noun destinations + one create button — nothing more (§2.1, Hick's Law).
 *  Documents lost top-level billing (IA, July 2026): both chat intakes upload
 *  natively and the case file's Documents tab is a document's home — the full
 *  library stays reachable via the user menu ("All documents") + mobile More. */
const LINKS = [
  { to: '/app', icon: Sun, label: 'Today', end: true, tour: 'nav-today' },
  { to: '/app/cases', icon: Briefcase, label: 'Cases', end: false, tour: 'nav-cases' },
  { to: '/app/research', icon: BookOpen, label: 'Research', end: false, tour: 'nav-research' },
  { to: '/app/drafts', icon: FileText, label: 'Drafts', end: false, tour: 'nav-drafts' },
  { to: '/app/clients', icon: Users, label: 'Clients', end: false, tour: 'nav-clients' },
];

const NAV_LINK_CLS = ({ isActive }: { isActive: boolean }) =>
  `flex items-center space-x-3 px-4 py-3 rounded-lg transition-all press ${
    isActive
      ? 'bg-accent-50 text-accent-700 border-l-4 border-accent shadow-elevation-1'
      : 'text-legal-600 hover:bg-legal-50 border-l-4 border-transparent'
  }`;

export function Sidebar() {
  const { user } = useAuthStore();
  const [createOpen, setCreateOpen] = useState(false);

  const isFreeTier = !user?.subscription_tier || user.subscription_tier === 'free';

  return (
    <aside className="hidden lg:flex lg:flex-col w-64 bg-white border-r border-legal-200 min-h-[calc(100vh-64px)]">
      {/* ＋ New — the only verb in the nav */}
      <div className="p-4 pb-2">
        <div className="relative">
          <button
            type="button"
            data-tour="new-button"
            data-track="nav-new"
            aria-haspopup="menu"
            aria-expanded={createOpen}
            onClick={() => setCreateOpen((v) => !v)}
            className="w-full flex items-center justify-center gap-2 bg-accent text-legal-900 font-semibold px-4 py-3 rounded-lg hover:bg-accent-400 hover:shadow-glow-accent transition-all press text-sm"
          >
            <Plus className="h-4 w-4" />
            <span>New</span>
          </button>
          {createOpen && <CreateMenu variant="popover" onClose={() => setCreateOpen(false)} />}
        </div>
      </div>

      <nav className="p-4 pt-2 space-y-2 flex-1">
        {LINKS.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            end={link.end}
            data-tour={link.tour}
            data-track={link.tour}
            className={NAV_LINK_CLS}
          >
            <link.icon className="h-5 w-5" />
            <span className="font-medium">{link.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Bottom cluster: Settings + Upgrade (free tier, never in the TWA) */}
      <div className="p-4 space-y-2">
        <NavLink to="/app/settings" data-tour="nav-settings" data-track="nav-settings" className={NAV_LINK_CLS}>
          <Settings className="h-5 w-5" />
          <span className="font-medium">Settings</span>
        </NavLink>

        {isFreeTier && (
          <div data-tour="quota">
            {!isTwa() && (
              <Link
                to="/pricing"
                data-track="nav-upgrade"
                className="flex items-center space-x-2 w-full bg-accent text-legal-900 font-semibold px-4 py-3 rounded-lg hover:bg-accent-400 hover:shadow-glow-accent transition-all press text-sm"
              >
                <Sparkles className="h-4 w-4" />
                <span>Upgrade</span>
              </Link>
            )}
            <p className="text-xs text-legal-400 mt-2 text-center">
              {user?.monthly_jobs_used ?? 0}/{user?.monthly_jobs_limit ?? 3} researches used
            </p>
            {typeof user?.monthly_drafts_limit === 'number' && (
              <p className="text-xs text-legal-400 mt-1 text-center">
                {user?.monthly_drafts_used ?? 0}/{user.monthly_drafts_limit} standard drafts used
              </p>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
